
import { useState } from "react";
import { FileText } from "lucide-react";
import { Profile } from "@/hooks/useProfile";
import { Cast } from "@/hooks/useCasts";
import { Post } from "@/hooks/posts/types";
import { EditPostModal } from "./EditPostModal";
import { CropSection } from "./posts/CropSection";
import { TimeLapse } from "./TimeLapse";
import { toast } from "sonner";

interface PostsTabProps {
  profile: Profile;
  posts: Post[];
  casts: Cast[];
  onTimelapseClick: (crop: string) => void;
  onPostClick: (post: Post) => void;
  onToggleSolved?: (castId: string) => void;
  onEditPost?: (post: Post) => void;
  onDeletePost?: (postId: string) => void;
}

export const PostsTab = ({ 
  profile, 
  posts, 
  casts, 
  onTimelapseClick, 
  onPostClick, 
  onToggleSolved,
  onEditPost,
  onDeletePost 
}: PostsTabProps) => {
  const [editingPost, setEditingPost] = useState<Post | null>(null);
  const [selectedCropForTimelapse, setSelectedCropForTimelapse] = useState<string | null>(null);

  // More flexible user post filtering - check multiple ways to match the user's posts
  const userPosts = posts.filter(post => {
    // Direct user_id match (most reliable)
    if (post.user_id === profile?.user_id) {
      return true;
    }
    
    // Try matching by username if available
    if (profile?.username && post.user?.username === profile.username) {
      return true;
    }
    
    // Try matching by farm name if available  
    if (profile?.farm_name && post.user?.name === profile.farm_name) {
      return true;
    }
    
    // For mock posts, check if it's assigned to "Your Farm"
    if (post.user?.name === "Your Farm" && post.user_id === profile?.user_id) {
      return true;
    }
    
    return false;
  });

  console.log('Profile matching debug:', { 
    profile_user_id: profile?.user_id, 
    profile_username: profile?.username, 
    profile_farm_name: profile?.farm_name,
    total_posts: posts.length,
    user_posts_found: userPosts.length,
    sample_post_user: posts[0]?.user,
    sample_post_user_id: posts[0]?.user_id
  });

  const postsByCrop = userPosts.reduce((acc, post) => {
    const crop = post.crop || post.crop_type || 'Other';
    if (!acc[crop]) {
      acc[crop] = [];
    }
    acc[crop].push(post);
    return acc;
  }, {} as Record<string, typeof userPosts>);

  // Sort posts by timestamp within each crop
  Object.keys(postsByCrop).forEach(crop => {
    postsByCrop[crop].sort((a, b) => {
      const getTimeValue = (timestamp: string) => {
        if (timestamp.includes('hour')) return parseInt(timestamp) * 60;
        if (timestamp.includes('day')) return parseInt(timestamp) * 1440;
        if (timestamp.includes('week')) return parseInt(timestamp) * 10080;
        return 0;
      };
      return getTimeValue(b.timestamp) - getTimeValue(a.timestamp);
    });
  });

  const getVideoPostsForCrop = (crop: string) => {
    return postsByCrop[crop]?.filter(post => post.isVideo) || [];
  };

  const handleEditPost = (post: Post) => {
    setEditingPost(post);
  };

  const handleSaveEdit = (updatedPost: Post) => {
    if (onEditPost) {
      onEditPost(updatedPost);
      toast.success("Post updated successfully!");
    }
  };

  const handleDeletePost = async (postId: string) => {
    if (!onDeletePost) return;
    try {
      await onDeletePost(postId);
      toast.success("Post deleted successfully!");
    } catch (err) {
      console.error('Delete post failed:', err);
      toast.error("Failed to delete post. Please try again.");
    }
  };

  const handleTimelapseClick = (crop: string) => {
    setSelectedCropForTimelapse(crop);
    onTimelapseClick(crop);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-green-800">Your Posts</h3>
      </div>

      {userPosts.length === 0 ? (
        <div className="text-center py-8">
          <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500 mb-2">No posts yet</p>
          <p className="text-sm text-gray-400">Share your farming journey with the community!</p>
          <div className="mt-4 text-xs text-gray-400 p-4 bg-gray-50 rounded-lg">
            <p className="font-medium mb-2">Debug info:</p>
            <p>Total posts in system: {posts.length}</p>
            <p>Your user ID: {profile?.user_id}</p>
            <p>Your username: {profile?.username}</p>
            <p>Your farm name: {profile?.farm_name}</p>
            {posts.length > 0 && (
              <>
                <p className="mt-2">Sample post user info:</p>
                <p>- User ID: {posts[0]?.user_id}</p>
                <p>- Username: {posts[0]?.user?.username}</p>
                <p>- Name: {posts[0]?.user?.name}</p>
              </>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(postsByCrop).map(([crop, cropPosts]) => (
            <CropSection
              key={crop}
              crop={crop}
              posts={cropPosts}
              videoPostsCount={getVideoPostsForCrop(crop).length}
              onTimelapseClick={handleTimelapseClick}
              onPostClick={onPostClick}
              onEditPost={handleEditPost}
              onDeletePost={handleDeletePost}
              showActions={true}
            />
          ))}
        </div>
      )}

      <EditPostModal
        post={editingPost}
        isOpen={!!editingPost}
        onClose={() => setEditingPost(null)}
        onSave={handleSaveEdit}
      />

      {selectedCropForTimelapse && (
        <TimeLapse
          crop={selectedCropForTimelapse}
          posts={userPosts}
          onClose={() => setSelectedCropForTimelapse(null)}
        />
      )}
    </div>
  );
};
